/**
 * ArsenalModal — Full-screen modal overlay for "Load Your Arsenal"
 * Renders the ArsenalContent component inside a modal instead of navigating away.
 */
import { createContext, useContext, useState, useCallback, lazy, Suspense } from "react";
import { X, Loader2 } from "lucide-react";

// Lazy-load the heavy Arsenal content
const ArsenalPage = lazy(() => import("@/pages/Arsenal"));

/* ── Global context so any component can open the modal ── */
interface ArsenalModalContextValue {
  openArsenal: () => void;
}

const ArsenalModalContext = createContext<ArsenalModalContextValue>({
  openArsenal: () => {},
});

export const useArsenalModal = () => useContext(ArsenalModalContext);

export function ArsenalModalProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  const openArsenal = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <ArsenalModalContext.Provider value={{ openArsenal }}>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[1000] flex items-start justify-center overflow-y-auto"
          style={{
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(4px)",
            padding: "40px 24px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            className="w-full relative"
            style={{
              maxWidth: 960,
              background: "#0d1117",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 16,
              padding: "40px 48px 48px",
              margin: "auto",
            }}
          >
            {/* Close button */}
            <button
              onClick={close}
              className="absolute flex items-center justify-center cursor-pointer"
              style={{
                top: 20,
                right: 24,
                width: 32,
                height: 32,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                color: "rgba(255,255,255,0.5)",
                transition: "background 0.15s, color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                e.currentTarget.style.color = "rgba(255,255,255,0.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                e.currentTarget.style.color = "rgba(255,255,255,0.5)";
              }}
              aria-label="Close arsenal"
            >
              <X className="w-4 h-4" />
            </button>

            <Suspense
              fallback={
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              }
            >
              {/* Render the Arsenal page content directly, 
                  it uses DashboardLayout internally which is a thin wrapper */}
              <ArsenalModalContent />
            </Suspense>
          </div>
        </div>
      )}
    </ArsenalModalContext.Provider>
  );
}

/**
 * ArsenalModalContent — imports and renders ArsenalContent (the inner component)
 * We import the full page but it wraps in DashboardLayout which is a passthrough
 * when already inside AppLayout. So it just renders the content.
 */
function ArsenalModalContent() {
  // We render the default export which includes DashboardLayout wrapper
  // DashboardLayout is a thin passthrough when nested in AppLayout context
  return <ArsenalPage />;
}
